import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import "./ValveTypes.css"; // Reusing the same styles

function ParameterValues() {
  const { valveTypeId, parameterId } = useParams();
  const [parameter, setParameter] = useState(null);
  const [valveType, setValveType] = useState(null);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editValue, setEditValue] = useState(null);
  const [valueToDelete, setValueToDelete] = useState(null);
  const [formData, setFormData] = useState({ value: "" });
  const [message, setMessage] = useState({ type: "", text: "" });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [parameterId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vtRes, paramRes, valuesRes] = await Promise.all([
        axios.get(`/valve-types/${valveTypeId}`),
        axios.get(`/valve-types/${valveTypeId}/parameters/${parameterId}`),
        axios.get(`/valve-type-parameters/${parameterId}/values`),
      ]);
      setValveType(vtRes.data.data);
      setParameter(paramRes.data.data);
      setValues(valuesRes.data.data || []);
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const openAddModal = () => {
    setEditValue(null);
    setFormData({ value: "" });
    setShowModal(true);
  };

  const openEditModal = (val) => {
    setEditValue(val);
    setFormData({ value: val.value });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    setFormData({ value: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editValue) {
        await axios.put(
          `/valve-type-parameters/${parameterId}/values/${editValue.id}`,
          { value: formData.value }
        );
        showMessage("success", "Value updated successfully");
      } else {
        await axios.post(`/valve-type-parameters/${parameterId}/values`, {
          value: formData.value,
        });
        showMessage("success", "Value added successfully");
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `/valve-type-parameters/${parameterId}/values/${valueToDelete.id}`
      );
      showMessage("success", "Value deleted successfully");
      setValueToDelete(null);
      fetchData();
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Failed to delete value");
    }
  };

  return (
    <div className="valve-types-container">
      <div className="valve-types-header">
        <div>
          <button
            className="btn-back"
            onClick={() =>
              navigate(`/admin/valve-configuration/${valveTypeId}/parameters`)
            }
          >
            ← Back to Parameters
          </button>
          <nav className="breadcrumb" style={{ marginTop: "4px", fontSize: "13px", color: "#64748b" }}>
            Valve Configuration &gt; {valveType?.name} &gt; {parameter?.name}
          </nav>
          <h1 className="valve-types-title">
            {parameter?.name} — Value List
          </h1>
          <p className="valve-types-subtitle">
            Manage dropdown values for this parameter
          </p>
        </div>
        <button className="btn-add-valve" onClick={openAddModal}>
          + Add Value
        </button>
      </div>

      {message.text && (
        <div className={`alert-message alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="loading-container">Loading values...</div>
      ) : (
        <div className="table-card">
          <div className="table-responsive">
            <table className="valve-types-table">
              <thead>
                <tr>
                  <th>Value</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {values.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="no-data-row">
                      No values found. Click "+ Add Value" to get started.
                    </td>
                  </tr>
                ) : (
                  values.map((val) => (
                    <tr key={val.id}>
                      <td className="valve-name-cell">{val.value}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => openEditModal(val)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => setValueToDelete(val)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Value Popup (3.A.5) */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              {editValue ? "Edit Value" : "Add Value"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  Value <span className="required-mark">*</span>
                </label>
                <input
                  className="form-input"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  placeholder="e.g. ASTM A216 Gr.WCB"
                  required
                  maxLength={200}
                  autoFocus
                />
                <span className="help-text">
                  Max 200 characters. Must be unique within this parameter (case-insensitive).
                </span>
              </div>
              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  type="button"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button className="btn-submit" type="submit">
                  {editValue ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {valueToDelete && (
        <div className="modal-overlay" onClick={() => setValueToDelete(null)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirm-title">Confirm Deletion</h2>
            <p className="confirm-text">
              Are you sure you want to delete the value "
              <strong>{valueToDelete.value}</strong>"?
            </p>
            <div className="confirm-info">
              If this value is used in existing TCDS records, deletion will be blocked.
            </div>
            <div className="confirm-actions">
              <button
                className="btn-cancel"
                onClick={() => setValueToDelete(null)}
              >
                Cancel
              </button>
              <button className="btn-confirm-delete" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParameterValues;
